import {
  CardElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import BeatLoader from 'react-spinners/BeatLoader';

import ccIcon from 'images/cc-icons.svg';

import { useSubscribe } from '../../../hooks';
import { RECAPTCHA_SITE_KEY } from '../../../src/env';
import { CurrentPlanSelection } from '../../../utils/backend-api/subscribe';
import { TextField } from '../../FormFields';
import Disclosure from '../../shared/Stripe/Disclosure';
import Schema from './Schema.js';
import styles from './paymentInfo.module.css';

const DARK_MODE_SPINNER_COLOR = '#fff';
const LIGHT_MODE_SPINNER_COLOR = '#d04237';

interface FormValues {
  email: string;
  name: string;
  password: string;
}

const PaymentInfo = ({
  currentPlan,
  referringSubGuid,
  referringSubName,
  currentTrialEndDate,
  prefilledUserName,
  prefilledUserEmail,
}: {
  currentPlan: CurrentPlanSelection;
  referringSubGuid: string;
  referringSubName?: string;
  currentTrialEndDate: string;
  prefilledUserName?: string;
  prefilledUserEmail?: string;
}) => {
  const initialState = useMemo(
    () => ({
      email: prefilledUserEmail || '',
      name: prefilledUserName || '',
    }),
    [prefilledUserEmail, prefilledUserName]
  );
  const [stripeError, setErrorMessage] = useState('');
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [isProcessingPaymentRequest, setIsProcessingPaymentRequest] = useState(false);
  const [currentPaymentMethod, setCurrentPaymethodMethod] = useState('card');
  const [currentWalletType, setCurrentWalletType] = useState(null);
  const { mutate: subscribeUser, isLoading: isProcessing, isError } = useSubscribe();

  const stripe = useStripe();
  const elements = useElements();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();

  useEffect(() => {
    if (stripe && paymentRequest === null) {
      setIsProcessingPaymentRequest(true);
      const newPaymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: currentPlan.total,
        requestPayerEmail: true,
        requestPayerName: true,
      });

      newPaymentRequest.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(newPaymentRequest);
          if (result.googlePay) {
            setCurrentWalletType('googlePay');
          } else if (result.applePay) {
            setCurrentWalletType('applePay');
          }
        }
        setIsProcessingPaymentRequest(false);
      });
    }
    if (paymentRequest) {
      paymentRequest.update({ total: currentPlan.total });
    }

    // Process via Google Pay or Apple Pay
    if (paymentRequest) {
      paymentRequest.on('token', async (event) => {
        const stripeToken = event.token.id;
        const refToken = await reCaptchaRef.current.executeAsync('subscription');
        const formValues = await formRef.current;
        const sanitizedParamas = {
          email: formValues.values.email,
          name: formValues.values.name,
          password: formValues.values.password,
          stripeNounce: stripeToken,
          plan: currentPlan.plan,
          recaptchaToken: refToken,
          referrer_guid: referringSubGuid,
        };
        await subscribeUser(sanitizedParamas);
        isError ? event.complete('fail') : event.complete('success');
      });
    }
  }, [stripe, paymentRequest, currentPlan, isError, referringSubGuid, subscribeUser]);

  const validateCard = (event) => {
    if (event.error) {
      setErrorMessage(event.error.message);
    } else {
      setErrorMessage('');
    }
  };

  const handleSubmit = async ({ email, name, password }) => {
    const stripeToken = await stripe.createToken(elements.getElement(CardElement));
    // Todo handle Stripe Token failure
    if (stripeToken.error) {
      setErrorMessage(stripeToken.error.message);
      return;
    }
    const refToken = await reCaptchaRef.current.executeAsync('subscription');
    const sanitizedParamas = {
      email: email,
      name: name,
      password: password,
      stripeNounce: stripeToken.token.id,
      plan: currentPlan.plan,
      recaptchaToken: refToken,
      referrer_guid: referringSubGuid,
    };
    subscribeUser(sanitizedParamas);
  };

  return (
    <div className={clsx(styles.referralSubscription, 'container')}>
      <div className="row">
        <div
          className={clsx(
            styles.subscribeHeader,
            'col-xs-12 col-md-8 col-md-offset-2 col-lg-6 col-lg-offset-3'
          )}
        >
          Subscribe to The Information
        </div>
      </div>
      <div className="row">
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleSubmit}
          validationSchema={Schema}
          innerRef={formRef}
        >
          {(formik) => (
            <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
              <Form noValidate onSubmit={formik.handleSubmit}>
                <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
                  <div className={styles.subscribePaymentHeading}>
                    <div className={styles.subscribePaymentHeadingText}>Account Information</div>
                    <div className={styles.subscribePaymentHeadingLine}></div>
                  </div>
                  <TextField name="name" placeholder="Full Name" disabled={isProcessing} />
                  <TextField
                    name="email"
                    placeholder="Email"
                    disabled={prefilledUserEmail || isProcessing}
                  />
                  <TextField
                    name="password"
                    placeholder="Password"
                    type="password"
                    disabled={isProcessing}
                  />
                </div>
                {isProcessingPaymentRequest ? (
                  <BeatLoader color={LIGHT_MODE_SPINNER_COLOR} size={10} />
                ) : (
                  <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
                    <div className={styles.subscribePaymentHeading}>
                      <div className={styles.subscribePaymentHeadingText}>Payment Information</div>
                      <div className={styles.subscribePaymentHeadingLine}></div>
                    </div>
                    {paymentRequest ? (
                      <div className={styles.paymentOptionsContainer}>
                        <div className={styles.paymentOptions}>
                          <div
                            onClick={() => setCurrentPaymethodMethod('wallet')}
                            className={clsx(styles.options, styles.payOptions, {
                              [styles.active]: currentPaymentMethod === 'wallet',
                            })}
                          >
                            <i
                              className={`fa ${
                                currentWalletType === 'googlePay' ? 'fa-google' : 'fa-apple'
                              }`}
                            ></i>
                            <span className={styles.tabLable}>Pay</span>
                          </div>
                          <div
                            onClick={() => setCurrentPaymethodMethod('card')}
                            className={clsx(styles.options, styles.creditCardOptions, {
                              [styles.active]: currentPaymentMethod === 'card',
                            })}
                          >
                            <i className="fa fa-credit-card"></i>
                            <span className={styles.tabLable}>Credit Card</span>
                          </div>
                        </div>
                        <div className={styles.paymentTappedWrapper}>
                          {currentPaymentMethod == 'card' ? (
                            <>
                              <div className={styles.ccContent}>
                                <img src={ccIcon} width="200" height="22" />
                              </div>
                              <div className={styles.cardElementContainer}>
                                <div className={styles.cardContainer}>
                                  <CardElement onChange={validateCard} />
                                </div>
                                {stripeError && (
                                  <div className={styles.errorMessage}>{stripeError}</div>
                                )}
                              </div>
                            </>
                          ) : (
                            <PaymentRequestButtonElement options={{ paymentRequest }} />
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={styles.ccContent}>
                          <img src={ccIcon} width="200" height="22" />
                        </div>
                        <div className={styles.cardElementContainer}>
                          <div className={styles.cardContainer}>
                            <CardElement onChange={validateCard} />
                          </div>
                          {stripeError && <div className={styles.errorMessage}>{stripeError}</div>}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
                  <hr />
                  <div className={styles.subscribeTodaysTotalContainer}>
                    <div>Today&apos;s Total</div>
                    <div>
                      $0.00
                      <br />
                      <span className={styles.todaysTotalSubText}>
                        (${parseFloat(String(currentPlan.price)).toFixed(2)}/{currentPlan.cycle}{' '}
                        after free trial)
                      </span>
                    </div>
                  </div>
                  <hr />
                </div>
                <div
                  className={clsx(
                    `col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2`,
                    styles.chargeNote
                  )}
                >
                  By beginning your trial, we will be sharing your name with {referringSubName}, who
                  has referred you, and letting them know that you&apos;ve started enjoying The
                  Information.
                </div>
                <Disclosure />
                {currentPaymentMethod === 'card' && (
                  <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
                    <Button
                      disabled={!stripe || isProcessing || !formik.dirty}
                      type="submit"
                      className={styles.submitBtn}
                      id="submit"
                    >
                      {isProcessing ? (
                        <BeatLoader color={DARK_MODE_SPINNER_COLOR} size={10} />
                      ) : (
                        <>Start 14-Day Free Trial</>
                      )}
                    </Button>
                  </div>
                )}

                <div className="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
                  <div className={styles.chargeNote}>
                    if you cancel before the end of day on {currentTrialEndDate}, you will not be
                    charged.
                  </div>
                </div>
              </Form>
            </ReCAPTCHA>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PaymentInfo;
